import { Box, Tooltip, Typography } from "@mui/material";
import { ReimbursementSummary } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";

interface ReimbursementsForecastCellProps {
  /** Summary for this claimant; undefined when projection hasn't loaded. */
  summary: ReimbursementSummary | undefined;
  /** True while the projection endpoint is fetching. */
  isLoading: boolean;
}

/**
 * Replaces the YTD column on reimbursement docs. The "paid" amount is the
 * headline (it's the load-bearing number — what the carrier actually paid);
 * the requested amount is contextual sub-text underneath when it differs
 * from paid.
 *
 * Deliberately NOT styled as `requested → paid` like the YTD column. The
 * arrow visually implies a state transition (YTD-before becomes YTD-after on
 * approval), which is wrong here — these are parallel facts about the same
 * reimbursement record, not a before/after.
 */
export const ReimbursementsForecastCell = ({ summary, isLoading }: ReimbursementsForecastCellProps) => {
  if (isLoading) {
    return (
      <Typography variant="body2" color="text.disabled">
        …
      </Typography>
    );
  }
  if (!summary) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }

  const total = summary.new_reimbursement_count + summary.duplicate_on_file_count;
  const requested = summary.total_requested_amount;
  const reimbursed = summary.total_reimbursement_amount;
  const adjustment = requested - reimbursed;
  const hasAdjustment = Math.abs(adjustment) >= 0.01; // float-safe

  // Build the contextual caption. When paid == requested, the requested
  // figure is redundant — just show the row count. Otherwise show "of $X
  // requested" so reviewers can spot heavy adjustments at a glance.
  const rowSuffix = total === 1 ? "row" : "rows";
  const caption = hasAdjustment
    ? `of ${formatUSD(requested)} requested · ${total} ${rowSuffix}`
    : `${total} ${rowSuffix}`;

  // Tooltip wording branches on adjustment sign so we don't render
  // "$-100.00 held back" copy when the carrier overpaid (e.g. interest /
  // retro adjustments). Practically unreachable in stop-loss data but
  // cheap to handle.
  const tooltipTitle = hasAdjustment
    ? adjustment > 0
      ? `Carrier paid ${formatUSD(reimbursed)} of ${formatUSD(requested)} requested. ${formatUSD(adjustment)} held back as deductible / adjustments / denials.`
      : `Carrier paid ${formatUSD(reimbursed)} on ${formatUSD(requested)} requested. ${formatUSD(Math.abs(adjustment))} extra paid (interest / retro adjustments).`
    : `Carrier paid the full ${formatUSD(requested)} requested across ${total} ${rowSuffix}.`;

  return (
    <Tooltip title={tooltipTitle} placement="top" arrow>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.1 }}>
        <Typography variant="body2" fontWeight={600}>
          {formatUSD(reimbursed)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      </Box>
    </Tooltip>
  );
};
